import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown'; // 引入 react-markdown
import Layout from '../components/Layout'; // 引入 Layout 组件
import ReviewForm from '../components/ReviewForm'; // 引入 ReviewForm 组件
import { submitReview,formatDateForMySQL } from '../utils/api'; // 引入通用函数
import './ChatPage.css'; // 样式文件

const host = process.env.REACT_APP_API_HOST; // API 地址

function ChatPage() {
    const [sessions, setSessions] = useState([]); // 存储 chatId 列表
    const [sessionsData, setSessionsData] = useState({}); // 存储完整的会话数据对象
    const [selectedSession, setSelectedSession] = useState(null); // 当前选中的 session
    const [messages, setMessages] = useState([]); // 当前选中 session 的对话列表
    const [startDate, setStartDate] = useState(''); // 开始日期
    const [endDate, setEndDate] = useState(''); // 结束日期
    const [isFetching, setIsFetching] = useState(false); // 控制按钮状态
    const [editingMessage, setEditingMessage] = useState(null); // 当前正在编辑的消息

    // 设置默认日期为最近两天
    useEffect(() => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        setStartDate(yesterday.toISOString().split('T')[0]); // 设置为昨天
        setEndDate(today.toISOString().split('T')[0]); // 设置为今天
    }, []);

    const fetchSessions = async () => {
        try {
            if (!startDate || !endDate) {
                alert('请选择完整的开始日期和结束日期');
                return;
            }

            const start = new Date(startDate);
            const end = new Date(endDate);
            const diffInDays = (end - start) / (1000 * 60 * 60 * 24);
            if (diffInDays > 7) {
                alert('时间范围不能超过 7 天');
                return;
            }

            setIsFetching(true);
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No token found in local storage');
                navigate('/login');
                return;
            }

            const response = await axios.get(
                `${host}/api/conversationdata/process?start_date=${startDate}&end_date=${endDate}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            const data = response.data;

            setSessions(Object.keys(data)); // 提取 chatId 列表并存储为数组
            setSessionsData(data); // 存储完整的会话数据对象
            const firstChatId = Object.keys(data)[0];
            setSelectedSession(firstChatId); // 默认选中第一个 session
            setMessages(data[firstChatId]?.msg || []); // 默认加载第一个 session 的对话
        } catch (error) {
            console.error('获取数据失败:', error);
        } finally {
            setIsFetching(false);
        }
    };

    const handleSessionClick = (chatId) => {
        const sessionData = sessionsData[chatId]; // 从完整的会话数据对象中获取数据
        if (!sessionData) {
            console.error(`Session with chatId ${chatId} not found`);
            return;
        }
        setSelectedSession(chatId);
        setMessages(sessionData.msg || []); // 确保 msg 存在，默认为空数组
    };

    const handleEditClick = (message) => {
    
        setEditingMessage({
            id:  -1, // 如果有 ID，则传递 ID
            chat_data_id: -1,
            Query: message.user_text,
            Answer: message.ai_text,
            Author: localStorage.getItem('user_name'),
            Create_time: formatDateForMySQL(new Date()), // 转换后的时间格式
            review_conclusion: null,
            review_user_id: null,
            review_username: null,
            review_time: null
        });
    };

    const handleReviewSubmit = async (reviewData) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No token found in local storage');
                navigate('/login');
                return;
            }

            const resp = await submitReview(reviewData, token); // 调用通用函数
            setEditingMessage(null); // 关闭 ReviewForm
        } catch (error) {
            console.error('插入数据失败:', error);
            alert('插入数据失败，请稍后重试');
        }
    };

    const handleReviewCancel = () => {
        setEditingMessage(null); // 关闭 ReviewForm
    };

    const isSubmitDisabled = !startDate || !endDate; // 如果日期未选择完整，按钮不可用

    return (
        <Layout>
            <div className="chat-page">
                {/* 左侧导航栏 */}
                <div className="sidebar">
                    <h3>Chat Sessions</h3>
                    {/* 日期选择组件 */}
                    <div className="date-picker">
                        <label>
                            开始日期:
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </label>
                        <label>
                            结束日期:
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </label>
                        <button
                            className="submit-button"
                            onClick={fetchSessions}
                            disabled={isSubmitDisabled || isFetching} // 按钮禁用条件
                        >
                            {isFetching ? '加载中...' : '确定'}
                        </button>
                    </div>
                    <ul>
                        {Array.isArray(sessions) && sessions.map((chatId) => (
                            <li
                                key={chatId}
                                className={selectedSession === chatId ? 'active' : ''}
                                onClick={() => handleSessionClick(chatId)}
                            >
                                {chatId}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* 右侧对话展示 */}
                <div className="chat-content">
                    {Array.isArray(messages) && messages.map((msg, index) => (
                        <div key={index} className="message-row">
                            <div className="conversation">
                                {/* 用户的问题 */}
                                <div className="user-message">
                                    <div className="time">{msg.time}</div>
                                    <div className="text">{msg.user_text}</div>
                                </div>
                                {/* AI 的回答 */}
                                <div className="ai-message">
                                    <div className="time">Response Time: {msg.delta_time}s</div>
                                    <div className="text">
                                        <ReactMarkdown>{msg.ai_text}</ReactMarkdown> {/* 渲染 Markdown */}
                                    </div>
                                </div>
                            </div>
                            <button
                                className="edit-button"
                                onClick={() => handleEditClick(msg)}
                            >
                                编辑
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* 显示 ReviewForm */}
            {editingMessage && (
                <ReviewForm
                    item={editingMessage}
                    onSubmit={handleReviewSubmit}
                    onCancel={handleReviewCancel}
                />
            )}
        </Layout>
    );
}

export default ChatPage;